import { NotificationArrival } from "Components/NotificationArrival/NotificationArrival";
import React, { ReactNode, Suspense, useEffect, useRef, useState } from "react";
import styles from "./Layout.module.scss";
import Header from "Components/Header/Header";
import cn from "classnames";
import SidebarNew from "../SideBarNew/Sidebar";
import { useLocation } from "react-router-dom";
import { read, write } from "Service/storage";
import { CloseRounded } from "@mui/icons-material";
import { useInRouteDrawer } from "Components/RouteDrawer/RouteDrawerContext";
import { PageErrorBoundary } from "Components/ErrorBoundary/PageErrorBoundary";
import { FormSkeleton, PageSkeleton } from "./PageSkeleton";

type LayoutProps = { children?: ReactNode; hasSidebar?: boolean };

const LayoutM: React.FC<LayoutProps> = ({ children, hasSidebar = true }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => read("sidebarCollapsed") === "true",
  );
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);
  const closeButton = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    const updateMobile = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", updateMobile);
    return () => window.removeEventListener("resize", updateMobile);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      write("sidebarCollapsed", String(!current));
      return !current;
    });
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.key]);
  useEffect(() => {
    if (!menuOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      previousFocus?.focus();
    };
  }, [menuOpen]);

  return (
    <div className={styles.layout}>
      {hasSidebar && (
        <>
          {menuOpen && (
            <button
              type="button"
              className={styles.menuBackdrop}
              aria-label="Close navigation menu"
              onClick={() => setMenuOpen(false)}
            />
          )}
          <nav
            id="main-navigation"
            aria-label="Main navigation"
            className={cn(
              styles.layout__sidebar,
              collapsed && styles.layout__sidebar_collapsed,
              menuOpen && styles.menuOpen,
            )}
          >
            <button
              ref={closeButton}
              type="button"
              className={styles.closeMenu}
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <CloseRounded aria-hidden="true" />
            </button>
            <SidebarNew
              collapsed={collapsed && !isMobile}
              onToggleCollapsed={toggleCollapsed}
            />
          </nav>
        </>
      )}
      <div className={styles.layout__background}></div>
      <div
        className={cn(styles.layout__content, hasSidebar && styles.fullWidth)}
      >
        <Header
          onToggleMenu={
            hasSidebar ? () => setMenuOpen((open) => !open) : undefined
          }
          isMenuOpen={menuOpen}
        />
        <PageErrorBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <NotificationArrival>{children}</NotificationArrival>
          </Suspense>
        </PageErrorBoundary>
      </div>
    </div>
  );
};

/**
 * The app shell (sidebar, header) around a page. Inside a route drawer a
 * page shows only its content. A crash in the page stays inside the shell.
 * Not memoised: `children` is new on every render, so memo never skipped
 * one, and it kept Fast Refresh from swapping this component in open tabs.
 */
export const Layout: React.FC<LayoutProps> = (props) =>
  useInRouteDrawer() ? (
    <PageErrorBoundary>
      <Suspense fallback={<FormSkeleton />}>{props.children}</Suspense>
    </PageErrorBoundary>
  ) : (
    <LayoutM {...props} />
  );
